import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { GraduationCap, HeartStraight, Sparkle } from "@phosphor-icons/react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);

  // Se já estiver autenticado (ex.: usuário clicou "voltar" do navegador após login),
  // redireciona para a home do papel — não mostra o formulário nem desloga.
  useEffect(() => {
    if (user && user.role) {
      if (user.must_change_password) nav("/change-password", { replace: true });
      else nav("/", { replace: true });
    }
  }, [user, nav]);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password, remember);
      toast.success(`Bem-vindo, ${u.name}`);
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.message) || err.message);
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative overflow-hidden bg-[#F7F3EE]">
      <div className="blob w-[500px] h-[500px] bg-[#FFB86B]/60 -top-40 -left-40" />
      <div className="blob w-[400px] h-[400px] bg-[#1E5FFF]/30 top-1/3 -left-20" />

      <div className="flex flex-col justify-between px-6 sm:px-8 lg:px-16 py-6 sm:py-10 relative z-10 min-h-screen">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-2xl gradient-primary flex items-center justify-center shadow-lg">
            <GraduationCap size={22} color="white" weight="duotone" />
          </div>
          <div>
            <div className="font-heading font-black text-lg leading-none">Caminhos</div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">APP</div>
          </div>
        </div>

        <div className="max-w-md w-full">
          <div className="pill-badge bg-white/70 backdrop-blur border border-[#1E5FFF]/20 text-[#1E5FFF] mb-6">
            <Sparkle size={12} weight="fill" /> Tutoria • Vestibulares & Concursos
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] text-[#12143A]">
            Sua aprovação <br />
            começa com <span className="text-[#1E5FFF]">acompanhamento</span>.
          </h1>

          <form onSubmit={submit} className="mt-10 space-y-4" name="login-form" autoComplete="on">
            <div>
              <Label htmlFor="email" className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Email</Label>
              <Input id="email" name="email" data-testid="login-email" type="email"
                value={email} required autoComplete="username"
                onChange={(e) => setEmail(e.target.value)} placeholder="voce@escola.com"
                className="mt-2 h-14 rounded-2xl border-zinc-200 bg-white/90 backdrop-blur focus:border-[#1E5FFF] focus-visible:ring-[#1E5FFF]/20 text-base" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Senha</Label>
                <Link to="/forgot-password" data-testid="forgot-link"
                      className="text-xs font-semibold text-[#1E5FFF] hover:underline">Esqueci minha senha</Link>
              </div>
              <Input id="password" name="password" data-testid="login-password" type="password"
                value={password} required autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="mt-2 h-14 rounded-2xl border-zinc-200 bg-white/90 backdrop-blur focus:border-[#1E5FFF] focus-visible:ring-[#1E5FFF]/20 text-base" />
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer select-none">
              <Checkbox data-testid="remember-me" checked={remember} onCheckedChange={setRemember} id="remember" />
              <span>Lembrar-me neste dispositivo</span>
            </label>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
              Sem essa opção, sua sessão encerra ao fechar o navegador. Encerramos automaticamente após 30 minutos de inatividade.
            </p>
            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full h-14 rounded-2xl gradient-primary hover:opacity-90 text-white font-bold text-base shadow-[0_10px_30px_rgba(30,95,255,0.35)] hover:shadow-[0_16px_40px_rgba(30,95,255,0.45)] hover:-translate-y-0.5">
              {loading ? "Verificando…" : "Entrar na plataforma"}
            </Button>
            <p className="text-sm text-zinc-500 pt-2 text-center">
              <span className="text-[11px] text-zinc-400">Alunos e responsáveis só entram por link de convite do professor.</span>
            </p>
          </form>
        </div>

        <div className="flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
          <div className="flex items-center gap-2">
            <HeartStraight size={14} weight="fill" className="text-[#FF3D71]" /> Feito por professores para levar você à aprovação
          </div>
          <Link to="/privacy" className="text-zinc-500 hover:text-[#1E5FFF] underline">Política de Privacidade</Link>
          <Link to="/terms" className="text-zinc-500 hover:text-[#1E5FFF] underline">Termos de Uso</Link>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <div className="absolute inset-6 rounded-[36px] overflow-hidden gradient-blush">
          <img src="https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1200&q=80"
               alt="students" className="w-full h-full object-cover mix-blend-multiply opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#1E5FFF]/30 via-transparent to-transparent" />
          <div className="absolute top-10 right-10 card-elevated px-5 py-4 max-w-[240px] fade-up" style={{animationDelay: '0.3s'}}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#1E5FFF]">Meta da semana</div>
            <div className="font-heading text-2xl font-black mt-1">18h 30m</div>
            <div className="text-xs text-zinc-500">estudo acompanhado</div>
            <div className="mt-3 flex items-center gap-1">
              <div className="h-1.5 flex-1 rounded-full bg-zinc-100 overflow-hidden">
                <div className="h-full w-3/4 gradient-primary rounded-full" />
              </div>
              <span className="text-xs font-mono-num font-bold text-[#1E5FFF]">75%</span>
            </div>
          </div>
          <div className="absolute bottom-24 left-10 card-elevated px-5 py-4 max-w-[260px] fade-up" style={{animationDelay: '0.5s'}}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full gradient-warm flex items-center justify-center">
                <Sparkle size={14} color="white" weight="fill" />
              </div>
              <div className="text-xs font-bold text-[#12143A]">Prof. tutor</div>
            </div>
            <p className="text-xs text-zinc-600 mt-2 leading-relaxed">"Revisei sua redação e separei 3 pontos para trabalharmos na próxima sessão. Vamos juntos!"</p>
          </div>
          <div className="absolute bottom-8 left-8 right-8 text-white">
            <div className="pill-badge bg-white/20 backdrop-blur border border-white/30 text-white">MANIFESTO</div>
            <p className="font-heading text-2xl font-black leading-tight mt-3 drop-shadow-lg">
              "Disciplina é a ponte entre metas e realizações."
            </p>
            <p className="text-xs opacity-80 mt-1">— Jim Rohn</p>
          </div>
        </div>
      </div>
    </div>
  );
}
